import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, X, Plus, CheckCircle, Search,
  MapPin, Globe, Users, ChevronRight, Zap, Lock,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = (extra = {}) => {
  const sid = localStorage.getItem("session_id");
  return {
    "Content-Type": "application/json",
    ...(sid ? { "X-Session-ID": sid } : {}),
    ...extra,
  };
};

const Logo = () => (
  <span className="text-xl font-black text-[#0F172A] font-['Outfit'] tracking-tight">ORDO AI</span>
);

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-2">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
          i < current   ? "bg-[#10B981] text-white" :
          i === current ? "bg-[#1E40AF] text-white" :
                          "bg-slate-100 text-slate-400"
        }`}>
          {i < current ? <CheckCircle className="w-4 h-4" /> : i + 1}
        </div>
        {i < total - 1 && (
          <div className={`w-8 h-0.5 transition-all duration-500 ${i < current ? "bg-[#10B981]" : "bg-slate-200"}`} />
        )}
      </div>
    ))}
  </div>
);

const PlanBadge = ({ plan }) => {
  if (!plan) return null;
  const isGrowth = plan === "growth";
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
      isGrowth ? "bg-[#1E40AF] text-white" : "bg-slate-100 text-slate-700"
    }`}>
      {isGrowth ? <Zap className="w-3 h-3" /> : null}
      {isGrowth ? "Growth Plan" : "Basic Plan"} selected
    </div>
  );
};

const Onboarding = () => {
  const navigate = useNavigate();

  const [plan, setPlan]     = useState(() => localStorage.getItem("plan_intent") || "basic");
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [step1Done, setStep1Done] = useState(false);
  const [step2Done, setStep2Done] = useState(false);

  // Step 1
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl]     = useState("");
  const [city, setCity]                 = useState("");
  const [audiences, setAudiences]       = useState([""]);

  // Step 2
  const [listings, setListings] = useState([""]);

  // Step 3
  const [competitorSearch, setCompetitorSearch] = useState("");
  const [competitors, setCompetitors]           = useState([]);
  const [searchResults, setSearchResults]       = useState([]);
  const [searching, setSearching]               = useState(false);

  // ── Audience helpers (NO autocomplete) ───────────────────────────
  const updateAudience = (i, val) => {
    const u = [...audiences]; u[i] = val; setAudiences(u);
  };
  const addAudience = () => {
    if (audiences.length < 5) setAudiences([...audiences, ""]);
  };
  const removeAudience = (i) => {
    if (audiences.length === 1) { setAudiences([""]); return; }
    setAudiences(audiences.filter((_, idx) => idx !== i));
  };
  const filledAudiences = audiences.filter(a => a.trim());

  // ── Listing helpers ───────────────────────────────────────────────
  const updateListing = (i, val) => {
    const u = [...listings]; u[i] = val; setListings(u);
  };
  const addListing = () => {
    if (listings.length < 5) setListings([...listings, ""]);
  };
  const removeListing = (i) => {
    if (listings.length === 1) { setListings([""]); return; }
    setListings(listings.filter((_, idx) => idx !== i));
  };
  const filledListings = listings.filter(l => l.trim());

  // ── Competitor helpers ────────────────────────────────────────────
  const searchCompetitors = async () => {
    if (!competitorSearch.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `${API}/competitors/search?q=${encodeURIComponent(competitorSearch)}&city=${encodeURIComponent(city)}`,
        { credentials: "include", headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      } else {
        setSearchResults([{ name: competitorSearch.trim(), url: "", city, id: `manual_${Date.now()}` }]);
      }
    } catch {
      setSearchResults([{ name: competitorSearch.trim(), url: "", city, id: `manual_${Date.now()}` }]);
    } finally {
      setSearching(false);
    }
  };

  const addCompetitor = (c) => {
    if (competitors.length >= 5) { toast.error("Maximum 5 competitors"); return; }
    if (competitors.find(x => x.id === c.id || x.name === c.name)) return;
    setCompetitors([...competitors, c]);
    setCompetitorSearch("");
    setSearchResults([]);
  };

  const addManualCompetitor = () => {
    if (!competitorSearch.trim()) return;
    if (competitors.length >= 5) { toast.error("Maximum 5 competitors"); return; }
    const c = { name: competitorSearch.trim(), url: "", city, id: `manual_${Date.now()}` };
    setCompetitors([...competitors, c]);
    setCompetitorSearch("");
    setSearchResults([]);
  };

  const removeCompetitor = (id) => setCompetitors(competitors.filter(c => c.id !== id));

  // ── Validation ────────────────────────────────────────────────────
  const canProceedStep0 = businessName.trim() && websiteUrl.trim() && city.trim();

  // ── Step 1 API ────────────────────────────────────────────────────
  const saveStep1 = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/onboarding/step1`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({
          business_name:    businessName.trim(),
          website_url:      websiteUrl.trim(),
          city:             city.trim(),
          target_audiences: filledAudiences,
          plan,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save. Please try again.");
        return false;
      }
      setStep1Done(true);
      return true;
    } catch {
      toast.error("Connection error. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── Step 2 API ────────────────────────────────────────────────────
  const saveStep2 = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/onboarding/step2`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({ existing_listings: filledListings }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save listings.");
        return false;
      }
      setStep2Done(true);
      return true;
    } catch {
      toast.error("Connection error. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── Step 3 API → trial → Stripe ───────────────────────────────────
  // FIXED: backend expects competitors as List[str] (URLs/names), not objects
  const handleComplete = async () => {
    setSaving(true);
    try {
      // Build competitor strings — use URL if available, else name
      const competitorStrings = competitors
       .map(c => c.url || c.name)
       .filter(Boolean);

      const step3Res = await fetch(`${API}/onboarding/step3`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({
          competitors: competitorStrings,
        }),
      });
      if (!step3Res.ok) {
        const err = await step3Res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to complete onboarding.");
        setSaving(false);
        return;
      }

      // Start trial
      await fetch(`${API}/trial/start`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({ plan }),
      }).catch(() => {}); // non-fatal

      // Create Stripe checkout
      const checkoutRes = await fetch(`${API}/payment/create-checkout`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify({ plan }),
      });

      if (checkoutRes.ok) {
        const data = await checkoutRes.json();
        if (data.url) {
          localStorage.removeItem("plan_intent");
          window.location.href = data.url;
          return;
        }
      }

      // Fallback — go to dashboard if Stripe fails
      toast.success("Welcome to ORDO AI! Your trial has started.");
      localStorage.removeItem("plan_intent");
      navigate("/dashboard");

    } catch {
      toast.error("Connection error. Please try again.");
      setSaving(false);
    }
  };

  // ── Next step ─────────────────────────────────────────────────────
  const nextStep = async () => {
    if (step === 0) {
      if (!canProceedStep0) {
        toast.error("Please fill in your business name, website, and city");
        return;
      }
      if (!step1Done) {
        const ok = await saveStep1();
        if (!ok) return;
      }
      setStep(1);
    } else if (step === 1) {
      if (!step2Done) {
        const ok = await saveStep2();
        if (!ok) return;
      }
      setStep(2);
    }
  };

  const skipStep = () => {
    if (step === 1) { setStep2Done(true); setStep(2); }
  };

  const stepTitles    = ["Your Business", "Online Presence", "Track Competitors"];
  const stepSubtitles = [
    "Tell us about your business and who you serve",
    "Add your existing listings so we can track your full presence",
    "See how you rank against your competition",
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Outfit'] flex flex-col">

      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 h-16 flex items-center px-6 shrink-0">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
          <Logo />
          <PlanBadge plan={plan} />
        </div>
      </nav>

      {/* CONTENT */}
      <div className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-lg">

          {/* Header */}
          <div className="text-center mb-8">
            <StepIndicator current={step} total={3} />
            <h1 className="text-2xl font-black text-[#0F172A] font-['Outfit'] mt-6 mb-1">
              {stepTitles[step]}
            </h1>
            <p className="text-slate-500 text-sm">{stepSubtitles[step]}</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="h-1.5 bg-[#1E40AF]" />
            <div className="p-6">

              {/* ── STEP 1 — Business info ── */}
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                      Business name <span className="text-red-400">*</span>
                    </label>
                    <input
                      value={businessName}
                      onChange={e => setBusinessName(e.target.value)}
                      placeholder="e.g. Miami Flow Plumbing"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                      Website URL <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Globe className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={websiteUrl}
                        onChange={e => setWebsiteUrl(e.target.value)}
                        placeholder="https://your-website.com"
                        className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                      City <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={city}
                        onChange={e => setCity(e.target.value)}
                        placeholder="e.g. Miami, FL"
                        className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                      />
                    </div>
                  </div>

                  {/* Target audiences — NO autocomplete dropdown */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-semibold text-[#0F172A]">
                        Target audiences <span className="text-slate-400 font-normal text-xs ml-1">up to 5</span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      {audiences.map((a, i) => (
                        <div key={i} className="relative">
                          <Users className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            value={a}
                            onChange={e => updateAudience(i, e.target.value)}
                            placeholder={`Audience ${i + 1} (e.g. Homeowners in Miami)`}
                            className="w-full h-11 pl-9 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                          />
                          {audiences.length > 1 && (
                            <button onClick={() => removeAudience(i)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-400 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {audiences.length < 5 && (
                      <button onClick={addAudience}
                        className="mt-2 flex items-center gap-1.5 text-xs text-[#1E40AF] font-semibold hover:opacity-70 transition-opacity">
                        <Plus className="w-3.5 h-3.5" /> Add audience
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── STEP 2 — Listings ── */}
              {step === 1 && (
                <div className="space-y-5">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs font-semibold text-[#1E40AF] mb-1">Optional — skip if you don't have any yet</p>
                    <p className="text-xs text-slate-600">
                      Add URLs for any listings you already have (Google Business, Yelp, etc.) so we can track and measure them.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-semibold text-[#0F172A]">
                        Existing listings <span className="text-slate-400 font-normal text-xs ml-1">up to 5</span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      {listings.map((l, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Globe className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              value={l}
                              onChange={e => updateListing(i, e.target.value)}
                              placeholder="https://yelp.com/biz/your-business"
                              className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                            />
                          </div>
                          {listings.length > 1 && (
                            <button onClick={() => removeListing(i)}
                              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:text-red-400 hover:border-red-200 transition-colors flex-shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {listings.length < 5 && (
                      <button onClick={addListing}
                        className="mt-3 flex items-center gap-1.5 text-xs text-[#1E40AF] font-semibold hover:opacity-70 transition-opacity">
                        <Plus className="w-3.5 h-3.5" /> Add listing
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── STEP 3 — Competitors ── */}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs font-semibold text-[#1E40AF] mb-1">Optional but highly recommended</p>
                    <p className="text-xs text-slate-600">
                      Businesses that track competitors improve their ORDO AI score 2x faster.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                      Search competitors <span className="text-slate-400 font-normal text-xs ml-1">up to 5</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          value={competitorSearch}
                          onChange={e => setCompetitorSearch(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && searchCompetitors()}
                          placeholder="Business name or website URL…"
                          className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                        />
                      </div>
                      <button
                        onClick={searchCompetitors}
                        disabled={searching || !competitorSearch.trim()}
                        className="h-11 px-4 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2 flex-shrink-0">
                        {searching
                          ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          : <Search className="w-4 h-4" />
                        }
                      </button>
                    </div>

                    {searchResults.length > 0 && (
                      <div className="mt-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        {searchResults.map((r, i) => (
                          <button key={i} onClick={() => addCompetitor(r)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0">
                            <div className="text-left">
                              <div className="text-sm font-semibold text-[#0F172A]">{r.name}</div>
                              {r.city && <div className="text-xs text-slate-500">{r.city}</div>}
                            </div>
                            <Plus className="w-4 h-4 text-[#1E40AF]" />
                          </button>
                        ))}
                        <button onClick={addManualCompetitor}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors text-[#1E40AF]">
                          <span className="text-sm font-semibold">Add "{competitorSearch}" manually</span>
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {competitorSearch.trim() && searchResults.length === 0 && !searching && (
                      <button onClick={addManualCompetitor}
                        className="mt-2 w-full flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors">
                        <span className="text-sm font-semibold text-[#1E40AF]">Add "{competitorSearch}"</span>
                        <Plus className="w-4 h-4 text-[#1E40AF]" />
                      </button>
                    )}
                  </div>

                  {competitors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        Tracking {competitors.length} competitor{competitors.length > 1 ? "s" : ""}
                      </p>
                      <div className="space-y-2">
                        {competitors.map(c => (
                          <div key={c.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                            <div>
                              <div className="text-sm font-semibold text-[#0F172A]">{c.name}</div>
                              {c.url && <div className="text-xs text-slate-500">{c.url}</div>}
                            </div>
                            <button onClick={() => removeCompetitor(c.id)} className="text-slate-300 hover:text-red-400 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {competitors.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">
                      No competitors added — you can always add them from your dashboard
                    </p>
                  )}

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                    <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-[#0F172A] mb-0.5">Secure payment next</p>
                      <p className="text-xs text-slate-500">
                        After this step you'll enter payment details via Stripe to lock in your 3-day free trial.
                        You won't be charged until day 4.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── BUTTONS ── */}
              <div className="flex gap-3 mt-8">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)}
                    className="px-5 py-3 border border-slate-200 rounded-xl text-sm text-slate-600 font-medium hover:bg-slate-50 transition-colors">
                    Back
                  </button>
                )}
                {step < 2 ? (
                  <button onClick={nextStep} disabled={saving}
                    className="flex-1 bg-[#1E40AF] hover:bg-[#1E3A8A] disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-105">
                    {saving
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <>Continue <ChevronRight className="w-4 h-4" /></>
                    }
                  </button>
                ) : (
                  <button onClick={handleComplete} disabled={saving}
                    className="flex-1 bg-[#10B981] hover:bg-[#059669] disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-105">
                    {saving
                      ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Setting up…</>
                      : <>Start my free trial <ArrowRight className="w-4 h-4" /></>
                    }
                  </button>
                )}
              </div>

              {step === 1 && (
                <div className="text-center mt-4">
                  <button onClick={skipStep} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                    Skip — I'll add listings later
                  </button>
                </div>
              )}
              {step === 2 && competitors.length === 0 && (
                <div className="text-center mt-4">
                  <button onClick={handleComplete} disabled={saving}
                    className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                    Skip competitors and start my trial
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
              <span>Step {step + 1} of 3 — {stepTitles[step]}</span>
              <span>{Math.round(((step + 1) / 3) * 100)}% complete</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5">
              <div className="bg-[#1E40AF] h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${((step + 1) / 3) * 100}%` }} />
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-slate-400">🕐 3-day free trial · No setup fees · Cancel anytime</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
